"use client";

import { useState } from "react";
import { Minus, Plus, TriangleAlert } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const tocItems = [{
  id: "m-01",
  index: "01",
  label: "SUPERVISED VISITATION"
}, {
  id: "m-02",
  index: "02",
  label: "MONITORED EXCHANGES"
}, {
  id: "m-03",
  index: "03",
  label: "COURT REPORTING"
}, {
  id: "m-04",
  index: "04",
  label: "SAFETY PROTOCOLS"
}, {
  id: "m-05",
  index: "05",
  label: "INTAKE REQUIREMENTS"
}, {
  id: "m-06",
  index: "06",
  label: "FEES & SCHEDULING"
}];
const manualEntries = [{
  id: "m-01",
  index: "M-01",
  title: "SUPERVISED VISITATION",
  instructions: ["Supervised parenting time provides a neutral, safe environment for children to maintain bonds.", "Our staff remains present at all times to ensure interactions remain child-centered and structured.", "We maintain firm boundaries to ensure the emotional and physical safety of all participants.", "Sessions are documented objectively to provide clear, neutral records for court review.", "Parents are expected to follow all court-ordered guidelines and agency policies during visits."],
  figure: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718652/img/supervised_visitation_session.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718652/img/supervised_visitation_session.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718652/img/supervised_visitation_session.png-wvc-sizes",
    alt: "Professional supervised parenting time environment",
    tag: "[FIG. 01 / NEUTRAL SPACE]"
  },
  caution: "Any violation of safety protocols may result in the immediate termination of the session."
}, {
  id: "m-02",
  index: "M-02",
  title: "MONITORED EXCHANGES",
  instructions: ["Monitored exchanges prevent direct contact between parents during child hand-offs.", "Our staff facilitates the transition of the child from one parent to the other in a neutral setting.", "We ensure that exchanges occur promptly and without conflict to minimize child stress.", "Documentation of arrival and departure times is provided for court records.", "Parents must arrive at their designated times to ensure a smooth, non-overlapping process."],
  figure: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718652/img/monitored_exchange_process.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718652/img/monitored_exchange_process.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718652/img/monitored_exchange_process.png-wvc-sizes",
    alt: "Structured child exchange process",
    tag: "[FIG. 02 / NEUTRAL HAND-OFF]"
  },
  caution: "Late arrivals may impact the ability to complete the exchange as scheduled."
}, {
  id: "m-03",
  index: "M-03",
  title: "COURT REPORTING",
  instructions: ["We provide objective, factual observation reports detailing the events of each session.", "Reports are written from a neutral, trauma-informed perspective focused on the child's experience.", "Documentation includes arrival times, activities, and adherence to court-ordered guidelines.", "Reports are submitted to the court and relevant legal parties according to established timelines.", "We do not provide legal advice or recommendations; our role is strictly observational."],
  figure: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718652/img/observation_reporting_documentation.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718652/img/observation_reporting_documentation.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718652/img/observation_reporting_documentation.png-wvc-sizes",
    alt: "Professional documentation and reporting",
    tag: "[FIG. 03 / FACTUAL RECORD]"
  },
  caution: "Reports are strictly confidential and shared only with authorized legal parties."
}, {
  id: "m-04",
  index: "M-04",
  title: "SAFETY PROTOCOLS",
  instructions: ["Safety is our primary concern; all services are conducted in secure, monitored environments.", "We implement trauma-informed practices to ensure children feel secure during all interactions.", "Staff are trained to de-escalate potential conflicts and maintain a calm, professional atmosphere.", "We reserve the right to end any session if safety or court-ordered boundaries are compromised.", "All participants must adhere to our code of conduct to ensure a respectful environment."],
  figure: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718652/img/safety_and_compliance.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718652/img/safety_and_compliance.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718652/img/safety_and_compliance.png-wvc-sizes",
    alt: "Safety and compliance standards",
    tag: "[FIG. 04 / SECURE ENVIRONMENT]"
  },
  caution: "Threats or aggressive behavior will not be tolerated under any circumstances."
}, {
  id: "m-05",
  index: "M-05",
  title: "INTAKE REQUIREMENTS",
  instructions: ["The intake process begins with a review of court orders and relevant legal documentation.", "Both parents must complete individual intake interviews before services can commence.", "We require copies of all current court orders regarding custody and visitation.", "Intake ensures that all parties understand the agency's role, boundaries, and expectations.", "Once intake is complete, we will coordinate scheduling based on court-ordered frequency."],
  figure: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718652/img/intake_process_steps.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718652/img/intake_process_steps.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718652/img/intake_process_steps.png-wvc-sizes",
    alt: "Steps for the intake process",
    tag: "[FIG. 05 / INTAKE FLOW]"
  },
  caution: "Services cannot begin until all intake documentation is finalized and approved."
}, {
  id: "m-06",
  index: "M-06",
  title: "FEES & SCHEDULING",
  instructions: ["Fees are established based on the type of service and the complexity of the court order.", "Payment is required in advance of scheduled sessions to confirm the appointment.", "Cancellations must be made within the required timeframe to avoid late fees.", "We work to accommodate court-ordered schedules while maintaining agency capacity.", "Detailed fee schedules are provided during the intake process for transparency."],
  figure: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718652/img/scheduling_and_fees.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718652/img/scheduling_and_fees.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718652/img/scheduling_and_fees.png-wvc-sizes",
    alt: "Scheduling and fee transparency",
    tag: "[FIG. 06 / SERVICE TERMS]"
  },
  caution: "Failure to provide timely notice for cancellations may result in service charges."
}];
export default function CourtProcessFaq() {
  const [activeId, setActiveId] = useState<string>("");
  const {
    ref: bannerRef,
    isVisible: bannerVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const handleTocClick = (id: string) => {
    const el = document.getElementById(`accordion-${id}`);
    if (el) {
      el.scrollIntoView({
        behavior: "smooth",
        block: "start"
      });
    }
  };
  return <section data-section-id="1770" id="courtprocessfaq" className="bg-background py-28 md:py-40">
      <div className="mx-auto max-w-[1360px] px-6 md:px-8 lg:px-16">
        <div ref={bannerRef} className={`transition-all duration-500 ease-out ${bannerVisible ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"}`}>
          <div className="flex items-center gap-3 mb-6">
            <span className="inline-block size-2.5 bg-primary" aria-hidden="true" />
            <span className="font-default text-xs md:text-sm font-semibold uppercase tracking-[0.18em] text-foreground">
              § 01 — Agency Standards
            </span>
          </div>

          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 md:gap-10">
            <h1 className="font-default font-bold uppercase leading-[0.88] tracking-tight text-foreground" style={{
            fontSize: "clamp(2.5rem, 8.2vw, 7.5rem)"
          }}>
              The{" "}
              <em className="not-italic md:italic font-default font-bold" style={{
              color: "var(--secondary)",
              fontStyle: "italic"
            }}>
                Professional
              </em>{" "}
              Guide To Our Services.
            </h1>
            <div className="shrink-0">
              <span className="inline-block font-default text-xs font-semibold uppercase tracking-[0.12em] text-foreground border border-foreground px-3 py-1.5">
                [REV. 2025.01 / AGENCY POLICY]
              </span>
            </div>
          </div>
        </div>

        <div className="mt-12 mb-16 h-6 w-full" style={{
        backgroundImage: "repeating-linear-gradient(45deg, var(--primary) 0, var(--primary) 2px, transparent 2px, transparent 8px), repeating-linear-gradient(-45deg, var(--primary) 0, var(--primary) 2px, transparent 2px, transparent 8px)",
        backgroundColor: "var(--background)",
        opacity: 0.4
      }} aria-hidden="true" />

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12">
          <aside className="lg:col-span-3">
            <div className="lg:hidden -mx-6 px-6 overflow-x-auto pb-4">
              <div className="flex gap-2 min-w-max">
                {tocItems.map((item, i) => <button key={item.id} data-index={i} onClick={() => handleTocClick(item.id)} className={`font-default text-xs font-semibold uppercase tracking-[0.12em] px-3 py-2 border whitespace-nowrap transition-colors ${activeId === item.id ? "border-secondary bg-secondary text-secondary-foreground" : "border-foreground/40 text-foreground hover:border-foreground"}`}>
                    {item.index} / {item.label}
                  </button>)}
              </div>
            </div>

            <div className="hidden lg:block sticky top-24">
              <div className="font-default text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground mb-6 flex items-center gap-2">
                <span className="inline-block size-2 bg-primary" aria-hidden="true" />
                Contents
              </div>
              <ul className="space-y-1">
                {tocItems.map((item, i) => {
                const isActive = activeId === item.id;
                return <li data-index={i} key={item.id}>
                      <button onClick={() => handleTocClick(item.id)} className={`group w-full text-left flex items-baseline gap-3 py-3 pr-2 border-l-2 transition-all duration-200 ${isActive ? "border-secondary pl-4" : "border-transparent pl-2 hover:border-foreground/40 hover:pl-3"}`}>
                        <span className="font-default text-xs font-semibold tracking-[0.12em] text-muted-foreground shrink-0">
                          {item.index} /
                        </span>
                        <span className={`font-default font-bold uppercase text-sm tracking-tight transition-colors ${isActive ? "text-foreground" : "text-foreground/80 group-hover:text-foreground"}`}>
                          {item.label}
                        </span>
                      </button>
                    </li>;
              })}
              </ul>
            </div>
          </aside>

          <div className="lg:col-span-9">
            <Accordion type="single" collapsible className="w-full" onValueChange={val => setActiveId(val || "")}>
              {manualEntries.map((entry, i) => <AccordionItem key={entry.id} value={entry.id} id={`accordion-${entry.id}`} data-index={i} className="border-t border-b-0 border-foreground last:border-b last:border-foreground data-[state=open]:border-l-4 data-[state=open]:border-l-secondary transition-all">
                  <AccordionTrigger className="hover:no-underline py-8 md:py-9 [&>svg]:hidden group">
                    <div className="grid grid-cols-[64px_1fr_40px] md:grid-cols-[96px_1fr_40px] gap-4 md:gap-6 items-center w-full">
                      <span className="font-default text-xs md:text-sm font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                        [{entry.index}]
                      </span>
                      <span className="font-default font-bold uppercase tracking-tight leading-none text-left text-foreground" style={{
                    fontSize: "clamp(1.25rem, 2.1vw, 2.125rem)"
                  }}>
                        {entry.title}
                      </span>
                      <span className="size-10 flex items-center justify-center bg-secondary text-secondary-foreground justify-self-end shrink-0">
                        <Plus className="size-5 group-data-[state=open]:hidden" strokeWidth={2.5} />
                        <Minus className="size-5 hidden group-data-[state=open]:block" strokeWidth={2.5} />
                      </span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="pb-10 pt-2">
                    <div className="md:ml-24 bg-card p-6 md:p-8 border border-foreground/15">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        <div>
                          <div className="font-default text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground mb-4 flex items-center gap-2">
                            <span className="inline-block size-1.5 bg-primary" aria-hidden="true" />
                            Procedure
                          </div>
                          <ol className="space-y-3 font-default text-base leading-relaxed text-foreground">
                            {entry.instructions.map((step, j) => <li key={j} data-index={j} className="grid grid-cols-[28px_1fr] gap-3">
                                <span className="font-default text-xs font-semibold tracking-[0.1em] text-muted-foreground pt-1">
                                  {String(j + 1).padStart(2, "0")}
                                </span>
                                <span>{step}</span>
                              </li>)}
                          </ol>
                          {entry.caution && <div className="mt-6 pt-4 border-t border-foreground/15">
                              <p className="font-default text-xs font-semibold uppercase tracking-[0.1em] flex items-start gap-2 text-foreground">
                                <TriangleAlert className="size-4 shrink-0 mt-0.5" style={{
                            color: "var(--secondary)"
                          }} strokeWidth={2.5} />
                                <span>Caution — {entry.caution}</span>
                              </p>
                            </div>}
                        </div>

                        <div className="relative">
                          <div className="relative">
                            <span className="absolute top-2 left-2 size-1.5 bg-foreground z-10" aria-hidden="true" />
                            <span className="absolute top-2 right-2 size-1.5 bg-foreground z-10" aria-hidden="true" />
                            <span className="absolute bottom-2 left-2 size-1.5 bg-foreground z-10" aria-hidden="true" />
                            <span className="absolute bottom-2 right-2 size-1.5 bg-foreground z-10" aria-hidden="true" />
                            <img src={entry.figure.src} data-wvc-srcset={entry.figure.srcset} data-wvc-sizes={entry.figure.sizes} alt={entry.figure.alt} loading="lazy" decoding="async" className="w-full h-auto object-cover border border-foreground/30" />
                          </div>
                          <div className="mt-3 flex items-center justify-between">
                            <span className="font-default text-[0.7rem] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                              {entry.figure.tag}
                            </span>
                            <span className="font-default text-[0.7rem] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                              REF. {entry.index}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>
          </div>
        </div>
      </div>
    </section>;
}